import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { readConsent, saveConsent } from '../lib/trackingConsent';
import { syncMetaConsent, trackMetaEvent } from '../lib/metaPixel';
import { analyticsPage, syncGoogleAnalytics, trackGoogleEvent } from '../lib/googleAnalytics';

const OPEN_PREFERENCES_EVENT = 'certy:open-cookie-settings';

const AnalyticsConsent: React.FC = () => {
  const location = useLocation();
  const [choice, setChoice] = React.useState(readConsent);
  const [showPreferences, setShowPreferences] = React.useState(choice === null);
  const [analytics, setAnalytics] = React.useState(choice?.analytics || false);
  const [marketing, setMarketing] = React.useState(choice?.marketing || false);
  const lastGooglePage = React.useRef('');
  const lastMetaPage = React.useRef('');

  React.useEffect(() => {
    const openPreferences = () => {
      const saved = readConsent();
      setAnalytics(saved?.analytics || false);
      setMarketing(saved?.marketing || false);
      setShowPreferences(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, openPreferences);
  }, []);

  React.useEffect(() => {
    const consent = readConsent();
    syncGoogleAnalytics();
    syncMetaConsent();
    const googlePage = analyticsPage().page_location;
    const metaPage = `${window.location.pathname}${window.location.search}`;
    if (!consent?.analytics) lastGooglePage.current = '';
    if (!consent?.marketing) lastMetaPage.current = '';
    if (consent?.analytics && lastGooglePage.current !== googlePage) {
      if (trackGoogleEvent('page_view')) lastGooglePage.current = googlePage;
    }
    if (consent?.marketing && lastMetaPage.current !== metaPage) {
      if (trackMetaEvent('PageView')) lastMetaPage.current = metaPage;
    }
  }, [choice, location.pathname, location.search]);

  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a');
      const href = anchor?.getAttribute('href') || '';
      // Visiting the download page is not a download or an activated trial.
      if (/Certy-Setup-[\d.]+\.exe(?:$|\?)/i.test(href)) trackEvent('download_click', { source: location.pathname });
      if (href.includes('#pricing')) trackEvent('pricing_view', { source: location.pathname });
      if (href === '/docs' || href.startsWith('/docs/')) trackEvent('docs_click', { source: location.pathname });
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [location.pathname]);

  const save = (allowAnalytics: boolean, allowMarketing: boolean) => {
    const consent = saveConsent(allowAnalytics, allowMarketing);
    setChoice(consent);
    setAnalytics(allowAnalytics);
    setMarketing(allowMarketing);
    setShowPreferences(false);
  };

  if (!showPreferences) return null;
  return (
    <aside aria-label="Preferencias de cookies" className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-h-[85dvh] max-w-[760px] overflow-y-auto rounded-lg border border-[#b8c5bc] bg-white p-4 shadow-[0_18px_60px_rgba(7,17,13,0.22)] sm:bottom-5 sm:p-5">
      <p className="text-sm font-semibold text-[#07110d]">Tú eliges las cookies</p>
      <p className="mt-1 text-xs leading-5 text-[#526158]">
        Las necesarias permiten que la web funcione. Con tu permiso usamos Google Analytics para
        entender las visitas y Meta para medir anuncios y mostrar publicidad de Certy en Facebook
        e Instagram. Puedes cambiar tu elección desde el pie de página.{' '}
        <Link to="/privacidad" className="font-semibold text-[#15783a] underline underline-offset-2">Más información</Link>
      </p>
      <div className="my-4 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[#24352b]">
        <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" checked={analytics} onChange={event => setAnalytics(event.target.checked)} className="h-4 w-4 accent-[#15783a]" />Analítica (Google)</label>
        <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" checked={marketing} onChange={event => setMarketing(event.target.checked)} className="h-4 w-4 accent-[#15783a]" />Publicidad (Meta)</label>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <button type="button" onClick={() => save(false, false)} className="rounded-md border border-[#07110d] px-4 py-2.5 text-xs font-semibold text-[#07110d] hover:bg-[#f0f4f1]">Rechazar opcionales</button>
        <button type="button" onClick={() => save(analytics, marketing)} className="rounded-md border border-[#07110d] px-4 py-2.5 text-xs font-semibold text-[#07110d] hover:bg-[#f0f4f1]">Guardar selección</button>
        <button type="button" onClick={() => save(true, true)} className="rounded-md border border-[#07110d] bg-[#07110d] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#15783a]">Aceptar todas</button>
      </div>
    </aside>
  );
};

export const openAnalyticsPreferences = () => window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
export default AnalyticsConsent;
